import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import AdminLayout from '../../../components/feature/AdminLayout';
import {
  deleteRecruitmentJob,
  fetchRecruitmentJobs,
  setRecruitmentJobAction,
  type RecruitmentJob,
  type RecruitmentJobStatus,
} from '../../../api/recruitment.api';

const STATUS_CLASS: Record<RecruitmentJobStatus, string> = {
  draft: 'bg-slate-100 text-slate-700',
  published: 'bg-emerald-50 text-emerald-700',
  closed: 'bg-amber-50 text-amber-700',
  archived: 'bg-gray-100 text-gray-500',
};

export default function RecruitmentJobsPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [items, setItems] = useState<RecruitmentJob[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    const r = await fetchRecruitmentJobs({ search, status: status || undefined });
    setLoading(false);
    if (r.ok === false) {
      if (r.unauthorized) {
        navigate('/admin/login', { replace: true });
        return;
      }
      setError(r.message);
      return;
    }
    setItems(r.data);
  }, [navigate, search, status]);

  useEffect(() => {
    void load();
  }, [load]);

  const onAction = async (job: RecruitmentJob, action: 'publish' | 'hide' | 'close' | 'archive' | 'delete') => {
    if (action === 'delete') {
      if (!window.confirm(t('recruitment.jobs.confirm_delete'))) return;
      const r = await deleteRecruitmentJob(job.id);
      if (r.ok === false) {
        setError(r.message);
        return;
      }
      await load();
      return;
    }
    const r = await setRecruitmentJobAction(job.id, action);
    if (r.ok === false) {
      setError(r.message);
      return;
    }
    await load();
  };

  return (
    <AdminLayout>
      <div className="flex flex-col gap-5">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h1 className="text-xl font-black text-gray-900">{t('page.recruitment_jobs.title')}</h1>
            <p className="text-sm text-gray-500 mt-0.5">{t('page.recruitment_jobs.subtitle')}</p>
          </div>
          <button
            type="button"
            onClick={() => navigate('/recruitment/jobs/new')}
            className="px-4 py-2 rounded-xl bg-teal-600 text-white text-xs font-bold"
          >
            {t('recruitment.jobs.add')}
          </button>
        </div>

        <div className="rounded-2xl bg-white ring-1 ring-gray-100 overflow-hidden">
          <div className="p-3 border-b border-gray-100 flex flex-col sm:flex-row gap-2">
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') void load();
              }}
              placeholder={t('recruitment.jobs.search')}
              className="flex-1 rounded-xl border border-gray-200 px-3 py-2 text-sm"
            />
            <select
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              className="rounded-xl border border-gray-200 px-3 py-2 text-xs font-bold bg-white"
            >
              <option value="">{t('recruitment.filter.all')}</option>
              {(['draft', 'published', 'closed', 'archived'] as const).map((s) => (
                <option key={s} value={s}>
                  {t(`recruitment.job_status.${s}`)}
                </option>
              ))}
            </select>
            <button type="button" onClick={() => void load()} className="px-3 py-2 rounded-xl bg-gray-900 text-white text-xs font-bold">
              {t('recruitment.refresh')}
            </button>
          </div>

          {error ? <div className="m-3 rounded-xl bg-rose-50 text-rose-700 text-sm px-3 py-2">{error}</div> : null}

          <div className="overflow-x-auto">
            <table className="w-full text-xs min-w-[760px]">
              <thead className="bg-gray-50 text-gray-500">
                <tr>
                  {[t('recruitment.jobs.col.title'), t('recruitment.jobs.col.department'), t('recruitment.jobs.col.location'), t('recruitment.jobs.col.status'), t('recruitment.jobs.col.apps'), ''].map((h) => (
                    <th key={h || 'a'} className="text-right font-bold px-3 py-3 whitespace-nowrap">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={6} className="px-3 py-10 text-center text-gray-400">
                      {t('recruitment.loading')}
                    </td>
                  </tr>
                ) : items.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-3 py-10 text-center text-gray-400">
                      {t('recruitment.jobs.empty')}
                    </td>
                  </tr>
                ) : (
                  items.map((job) => (
                    <tr key={job.id} className="border-t border-gray-50 hover:bg-teal-50/30">
                      <td className="px-3 py-3">
                        <button type="button" className="font-bold text-gray-900 text-start" onClick={() => navigate(`/recruitment/jobs/${job.id}`)}>
                          {job.title}
                        </button>
                        <p className="text-[11px] text-gray-400 mt-0.5" dir="ltr">
                          /{job.slug}
                        </p>
                      </td>
                      <td className="px-3 py-3">{job.department}</td>
                      <td className="px-3 py-3">{job.location}</td>
                      <td className="px-3 py-3">
                        <span className={`px-2 py-1 rounded-full text-[10px] font-bold ${STATUS_CLASS[job.status]}`}>
                          {t(`recruitment.job_status.${job.status}`)}
                        </span>
                      </td>
                      <td className="px-3 py-3 font-bold">{job.applications_count ?? 0}</td>
                      <td className="px-3 py-3">
                        <div className="flex flex-wrap gap-1 justify-end">
                          {job.status !== 'published' ? (
                            <button type="button" className="px-2 py-1 rounded-lg bg-emerald-50 text-emerald-700 font-bold" onClick={() => void onAction(job, 'publish')}>
                              {t('recruitment.jobs.publish')}
                            </button>
                          ) : (
                            <button type="button" className="px-2 py-1 rounded-lg bg-slate-100 text-slate-700 font-bold" onClick={() => void onAction(job, 'hide')}>
                              {t('recruitment.jobs.hide')}
                            </button>
                          )}
                          <button type="button" className="px-2 py-1 rounded-lg bg-amber-50 text-amber-700 font-bold" onClick={() => void onAction(job, 'close')}>
                            {t('recruitment.jobs.close')}
                          </button>
                          <button type="button" className="px-2 py-1 rounded-lg bg-gray-100 text-gray-600 font-bold" onClick={() => void onAction(job, 'archive')}>
                            {t('recruitment.jobs.archive')}
                          </button>
                          <button type="button" className="px-2 py-1 rounded-lg bg-rose-50 text-rose-700 font-bold" onClick={() => void onAction(job, 'delete')}>
                            {t('actions.delete')}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
}
